'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import Image from 'next/image';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { publicStorageUrl } from '@kit/shared/storage';
import { cn } from '@kit/ui/utils';

/**
 * How far a finger must travel horizontally before it counts as a swipe rather
 * than a tap. 50px matches the native hero carousel (hero-section.tsx).
 */
const SWIPE_THRESHOLD_PX = 50;

interface SlideState {
  /** The slide the user selected. Drives the dots, aria, and what we fetch. */
  index: number;
  /**
   * The slide actually painted. Lags `index` until that image has resolved, so
   * a jump to a cold slide never blanks the card mid-download.
   */
  shown: number;
  /** Indices currently in the DOM. Only ever grows (within one `signature`). */
  mounted: number[];
  /** Indices whose <img> has resolved — loaded OR errored. Only ever grows. */
  resolved: number[];
  /**
   * Identity of the `images` array this state describes. When the caller swaps
   * the array on a live instance, `mounted`/`resolved` describe the OLD photos
   * and would wrongly mark a fresh, undownloaded slide as safe to paint — so
   * the state is rebuilt instead. It is also part of each slide's React key —
   * see the render — which is what makes that rebuild survivable.
   */
  signature: string;
}

/** The active slide plus both neighbours — what must be in the DOM to move. */
function neighbourhood(index: number, count: number) {
  return [index, (index + 1) % count, (index - 1 + count) % count];
}

function initialState(count: number, signature: string): SlideState {
  return {
    index: 0,
    shown: 0,
    mounted: neighbourhood(0, Math.max(count, 1)),
    resolved: [],
    signature,
  };
}

/**
 * Move to `next` (wrapping) and remember which slides are worth keeping in the
 * DOM: everything already shown, plus BOTH neighbours of the new position.
 *
 * Slides are absolutely stacked on a single rect, so `loading="lazy"` buys
 * nothing — mounting all of them means one card pulls its whole gallery the
 * moment it scrolls into view, and a grid of ten-image galleries pulls
 * hundreds of images at once.
 *
 * Both neighbours, not just the forward one: a slide inserted in the same
 * commit that makes it active has no pre-change style to transition from, so
 * it pops in unfaded once its bytes land. Pre-mounting only forwards would
 * make the very first "prev" press hit that cold path every time.
 *
 * Neighbour pre-mounting cannot cover a dot jump, though — with six images the
 * seed is {0,1,5}, so dots 2-4 are cold. For those, `shown` deliberately stays
 * on the outgoing slide: we start the download but keep the old photo up until
 * it resolves, rather than fading to an empty box.
 */
function advance(state: SlideState, next: number, count: number): SlideState {
  const index = ((next % count) + count) % count;
  const needed = neighbourhood(index, count);
  const shown = state.resolved.includes(index) ? index : state.shown;

  if (
    state.index === index &&
    state.shown === shown &&
    needed.every((i) => state.mounted.includes(i))
  ) {
    return state;
  }

  return {
    ...state,
    index,
    shown,
    mounted: Array.from(new Set([...state.mounted, ...needed])),
  };
}

/**
 * A slide's <img> settled. Paint whatever the user is currently waiting on —
 * not just this slide: on a slow connection auto-rotate can move `index` past
 * a slide before its bytes land, and checking only `i === state.index` would
 * leave the photo frozen while the dots march on.
 *
 * Errors count as resolved too — otherwise one broken URL would pin `shown` and
 * wedge the carousel on a photo the user already navigated away from.
 */
function markResolved(state: SlideState, i: number): SlideState {
  if (state.resolved.includes(i)) {
    return state;
  }

  const resolved = [...state.resolved, i];

  return {
    ...state,
    resolved,
    shown: resolved.includes(state.index) ? state.index : state.shown,
  };
}

interface ImageCarouselProps {
  /** Ordered image URLs. Renders nothing when empty. */
  images: string[];
  /** Accessible alt prefix (e.g. the court or venue name). */
  alt: string;
  /** Auto-advance interval in ms. Default 4000. */
  intervalMs?: number;
  className?: string;
  /**
   * `sizes` for the slide images. MUST be overridden to match the caller's
   * layout wherever `unoptimized` is false — that is the only case where Next
   * emits a srcSet for `sizes` to choose from (generateImgAttrs short-circuits
   * on `unoptimized`), and a too-small hint there ships an upscaled image.
   */
  sizes?: string;
  /**
   * Preload the first slide. Default true, which suits the one-or-few
   * carousels on a booking page. Pass false in card GRIDS — twenty cards each
   * emitting a `<link rel=preload>` is worse than no hint at all.
   */
  priority?: boolean;
  /**
   * Skip the Next.js image optimizer. Default true, matching the rest of the
   * facility-image stack (the public bucket already serves canvas-compressed
   * uploads). Pass false where the surface is LCP-sensitive and the extra
   * resizing is worth it.
   */
  unoptimized?: boolean;
}

/**
 * Self-contained image carousel (no external dep) shared by the customer-facing
 * court cards and location cards. Shows arrows + dots and auto-rotates when
 * there is more than one image.
 *
 * Auto-rotate: advances every `intervalMs`, but only while the carousel is ON
 * SCREEN and the next slide has already downloaded — so the dots can never
 * march ahead of a photo that is still loading, and cards below the fold don't
 * quietly mount their whole gallery on a timer. It also pauses on hover and on
 * keyboard focus, stops for good once the user navigates manually, and never
 * runs under `prefers-reduced-motion`.
 *
 * Touch: swipe past SWIPE_THRESHOLD_PX commits to the next/previous slide,
 * provided the gesture is more horizontal than vertical — otherwise a thumb
 * flick down a card grid would flip photos mid-scroll. Threshold-only, not
 * finger-following: the slides are absolutely stacked and cross-faded, so
 * there is no track to translate. That same absence of a track is why this
 * needs no `dir="ltr"` container (the native hero carousel needs one only
 * because its translate maths flips under RTL); the gesture is mirrored in
 * Arabic instead, so it keeps agreeing with the mirrored arrows.
 *
 * IMPORTANT: this renders INSIDE clickable cards (a court <Label>/radio, a
 * location Card with onClick). Every control stops propagation AND prevents
 * default so navigating the gallery never selects the court or picks the
 * venue, and a swipe swallows the click it would otherwise produce — while a
 * plain tap is left to bubble, so tapping the photo still activates the card.
 *
 * Two rules for callers:
 *  1. Never wrap this in an <a>. Arrows/dots are real <button>s and interactive
 *     content inside an anchor is invalid HTML. Put the anchor on the card's
 *     text instead, and give the card an onClick for the photo.
 *  2. Never cover it with an overlay (e.g. a stretched ::after link). The
 *     pointer would target the overlay, and hover-pause and swipe would both
 *     silently stop working.
 */
export function ImageCarousel({
  images,
  alt,
  intervalMs = 4000,
  className,
  sizes = '(max-width: 768px) 50vw, 33vw',
  priority = true,
  unoptimized = true,
}: ImageCarouselProps) {
  const { t, i18n } = useTranslation();
  const signature = images.join('|');
  const [slides, setSlides] = useState<SlideState>(() =>
    initialState(images.length, signature),
  );
  // One object for the four flags that gate auto-rotate, per the repo's React
  // guidance. `hovered` and `focused` stay SEPARATE FIELDS rather than one
  // "paused" boolean: collapsed, the last event would win, so tabbing to an
  // arrow and then sweeping the mouse away would resume the timer under a
  // focused control.
  const [ui, setUi] = useState({
    hovered: false,
    focused: false,
    inView: false,
    /** Once the user drives the carousel, stop auto-rotating for good. */
    engaged: false,
  });
  const setFlag = useCallback(
    (key: 'hovered' | 'focused' | 'inView' | 'engaged', value: boolean) => {
      setUi((prev) => (prev[key] === value ? prev : { ...prev, [key]: value }));
    },
    [],
  );

  const count = images.length;
  const multiple = count > 1;
  const paused = ui.hovered || ui.focused;

  // The caller swapped the photos on a live instance — rebuild rather than
  // carry stale `resolved` indices into a fresh array.
  if (slides.signature !== signature) {
    setSlides(initialState(count, signature));
  }

  // Clamp during render (not in an effect) so the active index stays valid if
  // the image list shrinks under us (e.g. a re-fetch) without an extra paint.
  const safeIndex = slides.index < count ? slides.index : 0;
  const safeShown = slides.shown < count ? slides.shown : 0;

  const rootRef = useRef<HTMLDivElement>(null);

  const prefersReducedMotion = useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    prefersReducedMotion.current = mq.matches;
    const onChange = (e: MediaQueryListEvent) => {
      prefersReducedMotion.current = e.matches;
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Only rotate what the visitor can actually see. Without this the timer keeps
  // mounting one more slide every tick on every card on the page, so the lazy
  // mounting above would decay into "everything, just later".
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => setFlag('inView', !!entry?.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [setFlag]);

  const go = useCallback(
    (next: number) => {
      setSlides((prev) => advance(prev, next, count));
    },
    [count],
  );

  // Auto-rotate timer. Disabled when: single image, off screen, paused
  // (hover/focus), the user has taken over, or reduced-motion is preferred.
  useEffect(() => {
    if (
      !multiple ||
      !ui.inView ||
      paused ||
      ui.engaged ||
      prefersReducedMotion.current
    ) {
      return;
    }
    const id = window.setInterval(() => {
      setSlides((prev) => {
        const next = (prev.index + 1) % count;
        // Hold this tick if the next photo hasn't downloaded yet. It is already
        // mounted (pre-mounted as a neighbour), so it is on its way; advancing
        // regardless is what desynchronises the dots from the picture.
        if (!prev.resolved.includes(next)) return prev;
        return advance(prev, next, count);
      });
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [multiple, ui.inView, paused, ui.engaged, count, intervalMs]);

  // Manual navigation: swallow the click so the court radio doesn't toggle, and
  // stop auto-rotating from here on.
  const navigate = useCallback(
    (e: React.MouseEvent, next: number) => {
      e.preventDefault();
      e.stopPropagation();
      setFlag('engaged', true);
      go(next);
    },
    [go, setFlag],
  );

  // ── Touch ────────────────────────────────────────────────────────────────
  // Thumb swipe. `touch-pan-y` on the container leaves vertical page scroll
  // alone. clientX deltas are physical, so the only direction-dependent part is
  // what "forward" means: in Arabic the arrows mirror (next sits on the left),
  // so the gesture mirrors with them.
  const touchStart = useRef({ x: 0, y: 0 });
  const touchDelta = useRef({ x: 0, y: 0 });
  // Set when a gesture crossed the threshold, so the click the browser fires on
  // touchend can be swallowed. Without this a swipe would also select the card
  // the carousel sits inside.
  const swiped = useRef(false);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStart.current = { x: touch?.clientX ?? 0, y: touch?.clientY ?? 0 };
    touchDelta.current = { x: 0, y: 0 };
    // Clear here, not only when a click consumes it: not every browser fires a
    // click after a swipe, and a flag left set would eat the next real tap.
    swiped.current = false;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchDelta.current = {
      x: (touch?.clientX ?? touchStart.current.x) - touchStart.current.x,
      y: (touch?.clientY ?? touchStart.current.y) - touchStart.current.y,
    };
  }, []);

  const handleTouchEnd = useCallback(() => {
    const { x, y } = touchDelta.current;
    touchDelta.current = { x: 0, y: 0 };

    // A tap, or a scroll that merely drifted sideways. This component lives in
    // long vertical card grids, so without the vertical test a thumb flick down
    // the page would flip photos AND permanently kill auto-rotate on that card.
    if (Math.abs(x) <= SWIPE_THRESHOLD_PX || Math.abs(x) <= Math.abs(y)) {
      return;
    }

    swiped.current = true;
    setFlag('engaged', true);

    const isRTL = i18n.language?.split('-')[0] === 'ar';
    const forward = isRTL ? x > 0 : x < 0;

    go(forward ? safeIndex + 1 : safeIndex - 1);
  }, [go, safeIndex, i18n.language, setFlag]);

  // The browser cancels the gesture (scroll takes over, call comes in): drop it
  // rather than letting a stale delta commit on the next touchend.
  const handleTouchCancel = useCallback(() => {
    touchDelta.current = { x: 0, y: 0 };
    swiped.current = false;
  }, []);

  // Runs before the card's own onClick because it captures.
  const handleClickCapture = useCallback((e: React.MouseEvent) => {
    if (!swiped.current) return;
    swiped.current = false;
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // After all hooks, so the guard can never change hook order. Callers each
  // keep their own zero-image placeholder; rendering an empty grey box here
  // would silently replace it.
  if (count === 0) {
    return null;
  }

  return (
    <div
      ref={rootRef}
      data-test="image-carousel"
      role="region"
      aria-roledescription={t('bookings:carouselRoleDescription')}
      aria-label={alt}
      className={cn(
        'bg-muted relative aspect-[4/3] w-full touch-pan-y overflow-hidden',
        className,
      )}
      onMouseEnter={() => setFlag('hovered', true)}
      onMouseLeave={() => setFlag('hovered', false)}
      onFocusCapture={() => setFlag('focused', true)}
      onBlurCapture={() => setFlag('focused', false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
      onClickCapture={handleClickCapture}
    >
      {/* Slides — the painted one is opacity-100; cross-fade for a calm
          transition that matches the auto-rotate cadence. Unvisited slides are
          left out of the DOM entirely (see `advance`) so they don't download
          until they're about to be shown. Note this keys off `safeShown`, not
          `safeIndex`: a cold destination keeps the outgoing photo up until it
          resolves. */}
      {images.map((src, i) =>
        slides.mounted.includes(i) ? (
          <Image
            // The signature is part of the key on purpose. Rebuilding state on a
            // gallery swap clears `resolved`, but keying on `src` alone lets
            // React REUSE the existing <img> for a URL that merely moved — and a
            // reused, already-complete image never fires onLoad again, so
            // `resolved` could never refill and `shown` would freeze for good.
            // Changing the key forces a remount, so the load callback runs.
            key={`${slides.signature}#${src}`}
            src={publicStorageUrl(src)}
            alt={`${alt} — ${i + 1}`}
            fill
            sizes={sizes}
            className={cn(
              'object-cover transition-opacity duration-500',
              i === safeShown ? 'opacity-100' : 'opacity-0',
            )}
            onLoad={() => setSlides((prev) => markResolved(prev, i))}
            onError={() => setSlides((prev) => markResolved(prev, i))}
            unoptimized={unoptimized}
            priority={priority && i === 0}
          />
        ) : null,
      )}

      {multiple && (
        <>
          {/* Prev / Next arrows. Mirror for RTL via logical start/end. */}
          <button
            type="button"
            aria-label={t('bookings:carouselPrevImage')}
            onClick={(e) => navigate(e, safeIndex - 1)}
            className="absolute start-1.5 top-1/2 z-10 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm transition-colors hover:bg-black/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
          </button>
          <button
            type="button"
            aria-label={t('bookings:carouselNextImage')}
            onClick={(e) => navigate(e, safeIndex + 1)}
            className="absolute end-1.5 top-1/2 z-10 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm transition-colors hover:bg-black/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <ChevronRight className="h-4 w-4 rtl:rotate-180" />
          </button>

          {/* Dots. The bar spans the card, so it must not eat taps: only the
              dots themselves take pointer events, everything else falls through
              to whatever the card put underneath. Each dot paints at 6px but
              carries a larger invisible ::before hit area — a 6px target is far
              under WCAG 2.2 SC 2.5.8, and the row sits at the bottom edge of a
              photo people are trying to tap. */}
          <div className="pointer-events-none absolute inset-x-0 bottom-1.5 z-10 flex items-center justify-center gap-2">
            {images.map((src, i) => (
              <button
                key={`${slides.signature}#${src}`}
                type="button"
                aria-label={t('bookings:carouselGoToImage', { index: i + 1 })}
                aria-current={i === safeIndex}
                onClick={(e) => navigate(e, i)}
                className={cn(
                  'pointer-events-auto relative h-1.5 rounded-full bg-white/60 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-white',
                  "before:absolute before:-inset-x-1 before:-inset-y-3 before:content-['']",
                  i === safeIndex ? 'w-4 bg-white' : 'w-1.5 hover:bg-white/80',
                )}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
